import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import type { Blocker, CreateBlockerInput, UpdateBlockerInput } from './types';

export const blockerKeys = {
  all: ['blockers'] as const,
  range: (start: string, end: string) => ['blockers', 'range', start, end] as const,
};

/** Busy intervals include blockers server-side, so both refresh together. */
function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: blockerKeys.all });
  qc.invalidateQueries({ queryKey: ['calendars', 'busy'] });
}

export function useBlockers(range: { start: Date; end: Date }) {
  const start = range.start.toISOString();
  const end = range.end.toISOString();
  return useQuery({
    queryKey: blockerKeys.range(start, end),
    queryFn: () =>
      api<Blocker[]>(
        `/api/blockers?ends_after=${encodeURIComponent(start)}` +
          `&starts_before=${encodeURIComponent(end)}`,
      ),
  });
}

export function useCreateBlocker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBlockerInput) =>
      api<Blocker>('/api/blockers', { method: 'POST', json: body }),
    onSuccess: () => invalidate(qc),
    onError: (err: Error) => toast.error(err.message || 'Could not add blocker'),
  });
}

export function useUpdateBlocker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & UpdateBlockerInput) =>
      api<Blocker>(`/api/blockers/${id}`, { method: 'PATCH', json: patch }),
    // Optimistic: a dragged blocker must not snap back to its old slot while
    // the round-trip finishes.
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: blockerKeys.all });
      const previous = qc.getQueriesData<Blocker[]>({ queryKey: blockerKeys.all });
      qc.setQueriesData<Blocker[]>({ queryKey: blockerKeys.all }, (old) =>
        old?.map((b) => (b.id === id ? { ...b, ...patch } : b)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous.forEach(([key, data]) => qc.setQueryData(key, data));
      toast.error('Could not move blocker');
    },
    onSettled: () => invalidate(qc),
  });
}

export function useDeleteBlocker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/api/blockers/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(qc),
    onError: () => toast.error('Could not remove blocker'),
  });
}
